import { css } from '@emotion/react'
import { NextPage, NextPageContext } from 'next'
import { mq } from 'plume-ui'
import { useEffect, useRef, useState } from 'react'
import download from 'downloadjs'
import { format } from 'date-fns'
import { CardTitle } from '@/components/atoms/CardTitle'
import { Loader } from '@/components/atoms/Loader'
import { ForEnterprise } from '@/components/common/ForEnterprise'
import { InvestorTemplate } from '@/components/pages/investor/InvestorTemplate'
import { CompanyCardList } from '@/components/pages/investor/CompanyCardList'
import { InvestmentInfo } from '@/components/pages/investor/InvestmentInfo'
import { InvestorBasicInfo } from '@/components/pages/investor/InvestorBasicInfo'
import { InvestorFunds } from '@/components/pages/investor/InvestorFunds'
import { InvestorNewsSection } from '@/components/pages/investor/InvestorNewsSection'
import { InvestorPeople } from '@/components/pages/investor/InvestorPeople'
import { allTabList } from '@/components/pages/investor/InvestorTabGroup'
import { LPInvestments } from '@/components/pages/investor/LPInvestments'
import { VBInvestments } from '@/components/pages/investor/VBInvestments'
import { ViewMoreLink } from '@/components/common/ViewMoreLink'
import {
  downloadInvestorCompanies,
  fetchInvestorSummary,
  getInvestorCompanies,
  getInvestorFull,
  getInvestorOverseasCompanies,
} from '@/lib/api/investor'
import { isCvc, isVc } from '@/lib/investor'
import {
  ContentfulNpStartupFloatingBanner,
  ContentfulNpStartupSlideArticle,
  ContentfulNpStartupStaticBanner,
  InvestorFullInfo,
  InvestorSummary,
  InvestorTabType,
  NPFeedSearchResult,
} from '../../../types'
import Error from '../../_error'
import {
  ActiveTabMap,
  createInViewEventQueue,
  setupActiveTabs,
  InViewForActiveTab,
} from '@/components/common/InviewForActiveTab'
import { useUser } from '../../../contexts/AuthContext'
import { isGuest } from '@/lib/user'
import { scrollToRef } from '@/lib/page'
import { CompanyData, OverseasCompanyData } from '../../../types/api'
import { fetchNPFeedsByInvestorShortname } from '@/lib/api/np/feed'
import { MobileGuestFloatingBanner } from '@/components/common/MobileGuestFloatingBanner'
import { throttle } from 'lodash'
import { ThumbnailFloatingBanner } from '@/components/common/ThumbnailFloatingBanner'
import {
  getNpStartupFloatingBanner,
  getNpStartupSlideBannerArticles,
  getNpStartupStaticBanner,
} from '@/lib/api/contentful'
import { MobileGuestSlideBanner } from '@/components/common/MobileGuestSlideBanner'
import { NewsPicksStaticBanner } from '@/components/common/npStartup/NewsPicksStaticBanner'
import { DomesticInvestmentTable } from '@/components/pages/investor/DomesticInvestmentTable'
import { OverseasInvestmentTable } from '@/components/pages/investor/molecules/OverseasInvestmentTable'
import { EnterpriseGuideTemplate } from '@/components/view/EnterpriseGuide'
import { trackEvent } from '@/lib/tracking'

type Props = {
  summary: InvestorSummary | undefined
  statusCode: number | null | undefined
  npFeeds: NPFeedSearchResult[]
  floatingBanners: ContentfulNpStartupFloatingBanner[] | null
  slideBanners: ContentfulNpStartupSlideArticle[] | null
  staticBanners: ContentfulNpStartupStaticBanner[] | null
}

export const INVESTOR_TOP_LIST_COUNT = 5
export const COMPANIES_PER_PAGE = 5
const DEFAULT_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

const [push, apply] = createInViewEventQueue()

const InvestorPage: NextPage<Props> = (props) => {
  const user = useUser()
  const [init, first] = setupActiveTabs(allTabList)
  const [activeTabs, setActiveTabs] =
    useState<ActiveTabMap<InvestorTabType>>(init())
  const [fullInfo, setFullInfo] = useState<InvestorFullInfo>()
  const [count, setCount] = useState(0)
  const [overseasCount, setOverseasCount] = useState(0)
  const [companies, setCompanies] = useState<CompanyData[]>()
  const [overseasCompanies, setOverseasCompanies] =
    useState<OverseasCompanyData[]>()
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [overseasPage, setOverseasPage] = useState(1)
  const [downloading, setDownloading] = useState(false)
  const tableRef = useRef(null)
  const [isPopupVisible, setIsPopupVisible] = useState(true)
  const [isFloatingBannerVisible, setIsFloatingBannerVisible] = useState(true)
  const isOverseas = (shortname: string) => shortname.startsWith('SPD')

  useEffect(() => {
    if (!props.summary || !props.summary.shortname) {
      return
    }
    setPage(1)
    setOverseasPage(1)
    if (!isOverseas(props.summary.shortname)) {
      getInvestorFull(props.summary.shortname).then((res) => {
        setFullInfo(res.data)
      })
    } else {
      const dummyFullData: InvestorFullInfo = {
        ...props.summary,
        companies: { count: 0, data: [] },
        people: {
          past: [],
          current: [],
        },
        news: {
          finance: { count: 0, data: [] },
          external: { count: 0, data: [] },
        },
      }
      setFullInfo(dummyFullData)
    }
  }, [props.summary?.shortname])

  useEffect(() => {
    if (props.summary?.shortname) {
      setLoading(true)
      const offset = COMPANIES_PER_PAGE * (page - 1)
      !isOverseas(props.summary.shortname) &&
        getInvestorCompanies(
          props.summary.shortname,
          COMPANIES_PER_PAGE,
          offset
        )
          .then((res) => {
            setCount(res.count)
            setCompanies(res.data)
          })
          .finally(() => {
            setLoading(false)
          })
    }
  }, [props.summary?.shortname, page])

  useEffect(() => {
    if (props.summary?.shortname) {
      const offset = COMPANIES_PER_PAGE * (overseasPage - 1)
      isOverseas(props.summary.shortname) &&
        getInvestorOverseasCompanies(
          props.summary.shortname,
          COMPANIES_PER_PAGE,
          offset
        ).then((res) => {
          setOverseasCompanies(res.data)
          setOverseasCount(res.count)
        })
    }
  }, [props.summary?.shortname, overseasPage])

  const handleChangeActive = (tab: InvestorTabType, inView: boolean) => {
    push(tab, inView, onTimeout)
  }

  const onTimeout = () => {
    setActiveTabs(apply(activeTabs))
  }

  const handleDownload = async () => {
    if (props.summary) {
      setDownloading(true)
      try {
        const res = await downloadInvestorCompanies(
          props.summary?.shortname,
          'xlsx'
        )
        const success = download(
          await res.blob(),
          `${props.summary.name}_投資先_${timestamp()}.xlsx`,
          res.headers.get('content-type') ?? DEFAULT_MIME
        )
        trackEvent(user, '投資家の投資先のダウンロード')
        if (!success) {
          // TODO: Show error message
        }
      } finally {
        setDownloading(false)
      }
    }
  }

  const handleChangePage = (page: number) => {
    setPage(page)
    scrollToRef({ ref: tableRef, offset: 150 })
  }

  const handleChangeOverseasPage = (page: number) => {
    setOverseasPage(page)
  }

  const checkFooterInView = () => {
    const footer = document.getElementById('footer')
    if (footer === null) return
    const footerHeight = footer.offsetTop - window.innerHeight
    if (window.scrollY > footerHeight) {
      setIsPopupVisible(false)
    } else {
      setIsPopupVisible(true)
    }
  }

  const throttledCheckFooterInView = throttle(checkFooterInView, 200)

  useEffect(() => {
    window.addEventListener('scroll', throttledCheckFooterInView)
    return () => {
      window.removeEventListener('scroll', throttledCheckFooterInView)
    }
  }, [])

  useEffect(() => {
    const hash = location.hash.replace('#', '')
    if (hash) {
      setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView(true)
      })
    }
  }, [fullInfo, companies, overseasCompanies, user])

  if (props.statusCode === 403) {
    return <EnterpriseGuideTemplate />
  } else if (!props.summary || props.statusCode === 404) {
    return <Error />
  }

  return (
    <>
      <InvestorTemplate summary={props.summary} tab={first(activeTabs)}>
        <InvestorInviewForActiveTab
          tabName="basic-info"
          onChangeActive={handleChangeActive}
          css={css`
            padding-top: 24px;
            ${mq.untilMedium} {
              padding-top: 16px;
            }
          `}
        >
          <InvestorBasicInfo summary={props.summary} />
          {isGuest(user) && (
            <div
              css={css`
                margin: 64px 0 34px;
                ${mq.untilMedium} {
                  display: none;
                }
              `}
            >
              <NewsPicksStaticBanner
                staticBanner={
                  props.staticBanners ? props.staticBanners[0] : null
                }
                trackingId="investorpage-static-banner-1"
              />
            </div>
          )}
          {isGuest(user) && (
            <div
              css={css`
                margin-top: 16px;
                display: none;
                ${mq.untilMedium} {
                  display: block;
                }
              `}
            >
              <MobileGuestSlideBanner
                slideBannerArticles={props.slideBanners}
                trackingIdPrefix="investorpage"
              />
            </div>
          )}
        </InvestorInviewForActiveTab>
        {isVc(props.summary) && (
          <InvestorInviewForActiveTab
            tabName="investment-info"
            onChangeActive={handleChangeActive}
          >
            <InvestmentInfo summary={props.summary}></InvestmentInfo>
          </InvestorInviewForActiveTab>
        )}
        {fullInfo ? (
          <>
            <InvestorInviewForActiveTab
              tabName="companies"
              onChangeActive={handleChangeActive}
            >
              <CardTitle
                className="invested-companies"
                helpText="直接投資、あるいはGPとして運営しているファンド（二人組合形式を含む）から投資を行ったスタートアップ。現在は株式を保有していないスタートアップも含む。"
                helpProps={{ size: 'large' }}
              >
                投資先
              </CardTitle>
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 24px;
                `}
              >
                {!isOverseas(props.summary.shortname) && (
                  <DomesticInvestmentTable
                    companies={companies}
                    tableRef={tableRef}
                    loading={loading}
                    page={page}
                    count={count}
                    handleChangePage={handleChangePage}
                    downloading={downloading}
                    handleDownload={handleDownload}
                  />
                )}
                {isOverseas(props.summary.shortname) && (
                  <OverseasInvestmentTable
                    companies={overseasCompanies}
                    loading={false}
                    page={overseasPage}
                    count={overseasCount}
                    handleChangePage={handleChangeOverseasPage}
                  />
                )}
              </div>
            </InvestorInviewForActiveTab>
            {isCvc(fullInfo) && (
              <>
                <InvestorInviewForActiveTab
                  tabName="acquisitions"
                  onChangeActive={handleChangeActive}
                >
                  <CardTitle>買収</CardTitle>
                  <CompanyCardList list={fullInfo.acquisitions.data} />
                  <ViewMoreLink
                    isHidden={
                      fullInfo.acquisitions.count <= INVESTOR_TOP_LIST_COUNT
                    }
                    href={`/investor/acquisitions?shortname=${props.summary.shortname}`}
                    as={`/investors/${props.summary.shortname}/acquisitions`}
                    count={fullInfo.acquisitions.count}
                  />
                </InvestorInviewForActiveTab>
                <InvestorInviewForActiveTab
                  tabName="partnerships"
                  onChangeActive={handleChangeActive}
                >
                  <CardTitle>提携先</CardTitle>
                  <CompanyCardList list={fullInfo.partnerships.data} />
                  <ViewMoreLink
                    isHidden={
                      fullInfo.partnerships.count <= INVESTOR_TOP_LIST_COUNT
                    }
                    href={`/investor/partnerships?shortname=${props.summary.shortname}`}
                    as={`/investors/${props.summary.shortname}/partnerships`}
                    count={fullInfo.partnerships.count}
                  />
                </InvestorInviewForActiveTab>
              </>
            )}
            {isVc(fullInfo) && (
              <InvestorInviewForActiveTab
                tabName="seed-funded"
                onChangeActive={handleChangeActive}
              >
                <CardTitle>シード支援先</CardTitle>
                <CompanyCardList list={fullInfo.seeds.data} />
                <ViewMoreLink
                  isHidden={fullInfo.seeds.count <= INVESTOR_TOP_LIST_COUNT}
                  href={`/investor/seed-funded?shortname=${props.summary.shortname}`}
                  as={`/investors/${props.summary.shortname}/seed-funded`}
                  count={fullInfo.seeds.count}
                />
              </InvestorInviewForActiveTab>
            )}
            <InvestorInviewForActiveTab
              tabName="people"
              onChangeActive={handleChangeActive}
            >
              <InvestorPeople
                shortname={props.summary.shortname}
                people={fullInfo.people}
              />
            </InvestorInviewForActiveTab>
            <ForEnterprise>
              {fullInfo.funds ? (
                <InvestorInviewForActiveTab
                  tabName="funds"
                  onChangeActive={handleChangeActive}
                >
                  <InvestorFunds
                    list={fullInfo.funds.data}
                    investorShortname={props.summary.shortname}
                    helpText="GPとして、あるいは二人組合形式で運営しているファンド"
                    helpProps={{ size: 'large' }}
                  />
                  <ViewMoreLink
                    isHidden={fullInfo.funds.count <= INVESTOR_TOP_LIST_COUNT}
                    href={`/investor/funds?shortname=${props.summary.shortname}`}
                    as={`/investors/${props.summary.shortname}/funds`}
                    count={fullInfo.funds.count}
                  />
                </InvestorInviewForActiveTab>
              ) : null}
            </ForEnterprise>
            <ForEnterprise>
              {fullInfo.investments ? (
                <InvestorInviewForActiveTab
                  tabName="startup-investments"
                  onChangeActive={handleChangeActive}
                >
                  <VBInvestments
                    shortname={props.summary.shortname}
                    investments={fullInfo.investments}
                  />
                </InvestorInviewForActiveTab>
              ) : null}
            </ForEnterprise>
            <ForEnterprise>
              {fullInfo.investments ? (
                <InvestorInviewForActiveTab
                  tabName="lp-investments"
                  onChangeActive={handleChangeActive}
                >
                  <LPInvestments
                    investments={fullInfo.investments.lp.data}
                    shortname={props.summary.shortname}
                    helpText="LPとして出資したファンド"
                    helpProps={{ size: 'large' }}
                  />
                  <ViewMoreLink
                    isHidden={
                      fullInfo.investments.lp.count <= INVESTOR_TOP_LIST_COUNT
                    }
                    href={`/investor/details-lp?shortname=${props.summary.shortname}`}
                    as={`/investors/${props.summary.shortname}/details-lp`}
                    count={fullInfo.investments.lp.count}
                  />
                </InvestorInviewForActiveTab>
              ) : null}
            </ForEnterprise>
            {isGuest(user) && (
              <div
                css={css`
                  margin: 64px 0 34px;
                  ${mq.untilMedium} {
                    display: none;
                  }
                `}
              >
                <NewsPicksStaticBanner
                  staticBanner={
                    props.staticBanners ? props.staticBanners[1] : null
                  }
                  trackingId="investorpage-static-banner-2"
                />
              </div>
            )}
            <InvestorInviewForActiveTab
              tabName="news"
              onChangeActive={handleChangeActive}
            >
              <InvestorNewsSection
                shortname={props.summary.shortname}
                news={fullInfo.news}
              />
            </InvestorInviewForActiveTab>
          </>
        ) : (
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: center;
              padding-top: 30px;
            `}
          >
            <Loader />
          </div>
        )}
      </InvestorTemplate>
      {isGuest(user) && isPopupVisible && (
        <MobileGuestFloatingBanner trackingIdPrefix="investor-page" />
      )}
      {isGuest(user) && isFloatingBannerVisible && (
        <ThumbnailFloatingBanner
          onClose={() => setIsFloatingBannerVisible(false)}
          floatingBanner={props.floatingBanners}
          trackingIdPrefix="investor-page"
        />
      )}
    </>
  )
}

const InvestorInviewForActiveTab: React.FC<{
  tabName: InvestorTabType
  stickyOffset?: string
  className?: string
  children: React.ReactNode
  onChangeActive: (tab: InvestorTabType, inView: boolean) => void
}> = (props) => {
  return (
    <InViewForActiveTab<InvestorTabType>
      {...props}
      offset="-12em"
      mobileOffset="-5em"
      css={css`
        padding-block-start: 40px;
        ${mq.untilMedium} {
          padding-block-start: 32px;
        }
      `}
    />
  )
}

export const timestamp = () => {
  return format(new Date(), 'yyyyMMdd')
}

InvestorPage.getInitialProps = async (ctx: NextPageContext) => {
  const { shortname } = ctx.query
  const investorSummaryResponse = await fetchInvestorSummary(
    ctx,
    shortname!.toString()
  )
  const [
    summary,
    statusCode,
    npFeeds,
    floatingBanners,
    slideBanners,
    staticBanners,
  ] = await Promise.all([
    investorSummaryResponse?.data,
    investorSummaryResponse?.status,
    fetchNPFeedsByInvestorShortname(shortname as string),
    getNpStartupFloatingBanner(),
    getNpStartupSlideBannerArticles(),
    getNpStartupStaticBanner(),
  ])
  return {
    summary,
    statusCode,
    npFeeds,
    floatingBanners,
    slideBanners,
    staticBanners,
  }
}

export default InvestorPage
